import React, { useState } from 'react';
import { AddDesign } from './AddDesign';
import { useUsers } from '@/hooks';
import { leadService } from "../../../verve-leads/leads/services/leadService";
import { designService } from "../services/designService";
import { showSuccessMessage, showApiError } from "@/lib/toast";
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';

export const DesignerUpload = ({ isOpen, onClose, lead }) => {
  const { user } = useAuthStore();
  const [requestReview, setRequestReview] = useState(false);
  const [selectedDesigner, setSelectedDesigner] = useState('');
  
  const { teamMembers } = useUsers({ limit: 1000, excludeSelf: 'true' });
  const designers = teamMembers?.filter(u => {
      const isDesigner = u.role?.toLowerCase().includes('designer');
      const isNotSelf = u._id !== user?._id && u.id !== user?._id && u.name !== user?.name;
      return isDesigner && isNotSelf;
  }) || [];

  const queryClient = useQueryClient();

  const handleSubmit = async (formDataArray) => {
     try {
         // Upload files if any
         if (formDataArray && formDataArray.length > 0) {
             await Promise.all(formDataArray.map(fd => designService.uploadMedia(fd)));
         }

         if (requestReview && selectedDesigner) {
             // Send to main designer for review
             await leadService.update(lead._id, {
                 designer: selectedDesigner,
                 // Assuming we keep the status as initial_design but assigned to designer
             });
             showSuccessMessage("Design uploaded and sent to main designer for review");
         } else {
             // Complete upload and move to Initial Quotation
             await leadService.update(lead._id, {
                 status: 'initial_quotation'
             });
             showSuccessMessage("Design uploaded and moved to Initial Quotation");
         }
         
         // Invalidate queries to refresh lists
         queryClient.invalidateQueries({ queryKey: ['verve-contacts'] });
         queryClient.invalidateQueries({ queryKey: ['verve-leads'] });
         
         onClose();
     } catch (err) {
         showApiError(err);
     }
  }

  return (
      <AddDesign
         isOpen={isOpen}
         onClose={onClose}
         initialData={lead}
         targetStatus="initial_design"
         submitLabel={requestReview ? "Upload & Send for Review" : "Upload & Move to Initial Quotation"}
         titleOverride="UPLOAD INITIAL DESIGN"
         onSubmit={handleSubmit}
      >
         <div className="flex flex-col gap-2 mt-2 p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
               <input 
                  type="checkbox" 
                  checked={requestReview}
                  onChange={(e) => {
                      setRequestReview(e.target.checked);
                      if (!e.target.checked) setSelectedDesigner('');
                  }}
                  className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue w-4 h-4"
               />
               Request Main designer review
            </label>
            
            {requestReview && (
               <div className="mt-2 flex flex-col gap-1.5 animate-in fade-in slide-in-from-top-2 duration-200">
                  <label className="text-xs font-medium text-slate-700">Select Designer <span className="text-red-500">*</span></label>
                  <select 
                     value={selectedDesigner}
                     onChange={(e) => setSelectedDesigner(e.target.value)}
                     className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-brand-blue/20 bg-white"
                     required={requestReview}
                  >
                     <option value="">-- Select Designer --</option>
                     {designers.map(d => (
                        <option key={d._id} value={d._id}>{d.name}</option>
                     ))}
                  </select>
               </div>
            )}
         </div>
      </AddDesign>
  )
}
